import { addIntercomAppointmentParams, getParam } from '@/lib/utils';
import { store } from '@/store/store';
import { update as updateIntercom } from '@intercom/messenger-js-sdk';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

export function usePatientFromRoute() {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(true);
    const error = ref<string | null>(null);
    
    const patientId = computed(() => route.params.patientId as string);
    
    const patient = computed(() => {
        if (!patientId.value) return null;
        return store.members.find(m => m.pid === patientId.value);
    });
    
    async function loadPatientIfNeeded() {
        if (!patientId.value) {
            loading.value = false;
            return;
        }
        
        // if members are not loaded yet, wait for them
        if (store.members.length === 0) {
            // members should be loaded by the app, just wait
            const unwatch = watch(() => store.members, (members) => {
                if (members.length > 0) {
                    selectPatientFromMembers();
                    unwatch();
                }
            }, { immediate: true });
        } else {
            selectPatientFromMembers();
        }
    }
    
    function selectPatientFromMembers() {
        const foundPatient = store.members.find(m => m.pid === patientId.value);
        if (foundPatient) {
            // set appointment PID for the booking flow
            store.appointment.pid = foundPatient.pid;
            
            // update intercom with patient selection
            const patientIdExists = foundPatient.pid !== '';
            const bookedAppointment = store.app;
            const patientWantsToBookANewAppointment = (
                // if they're not editing an appointment, bookedAppointment will be empty
                // and it means we are booking a new appointment
                !bookedAppointment?.appID ||
                // they might've gone through the editing process, but changed the patient.
                // in this case, we need to reset the intercom params.
                bookedAppointment?.pid !== foundPatient.pid
            );

            if (patientIdExists && patientWantsToBookANewAppointment) {
                updateIntercom({
                    patient_id: foundPatient.pid,
                    ...addIntercomAppointmentParams(null),
                });
            }
            
            loading.value = false;
        } else {
            error.value = 'Patient not found';
            loading.value = false;
            // redirect to profiles page if patient not found
            router.push({ 
                name: 'profiles', 
                params: { domain: getParam(route) } 
            });
        }
    }
    
    onMounted(() => {
        loadPatientIfNeeded();
    });
    
    // watch for route changes
    watch(patientId, () => {
        loading.value = true;
        error.value = null;
        loadPatientIfNeeded();
    });
    
    return {
        patientId,
        patient,
        loading,
        error
    };
}